import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Boxes, Ship, Calculator, TrendingUp, ClipboardList,
  LogOut, Menu, X, AlertTriangle, Target,
} from "lucide-react";

const ROLE_LABEL = {
  logistik: "Dept Logistik",
  purchasing: "Dept Purchasing",
  keuangan: "Dept Keuangan",
  sales: "Dept Sales & Marketing",
  cabang: "Kantor Cabang",
};

function navFor(role) {
  const items = [{ to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" }];
  if (role === "logistik") items.push({ to: "/entry/stock", label: "Entry / Upload Stock", icon: Boxes, id: "entry-stock" });
  if (role === "purchasing") items.push({ to: "/entry/eta", label: "Entry / Upload ETA", icon: Ship, id: "entry-eta" });
  if (role === "keuangan") items.push({ to: "/entry/hpp", label: "Entry / Upload HPP", icon: Calculator, id: "entry-hpp" });
  if (role === "sales") items.push({ to: "/entry/sales", label: "Entry / Upload Penjualan", icon: TrendingUp, id: "entry-sales" });
  if (role === "cabang") items.push({ to: "/entry/projection", label: "Usulan Proyeksi Material", icon: ClipboardList, id: "entry-projection" });

  items.push({ to: "/laporan/stock", label: "Laporan Stock", icon: Boxes, id: "report-stock" });
  items.push({ to: "/laporan/penjualan", label: "Laporan Penjualan", icon: TrendingUp, id: "report-sales" });
  items.push({ to: "/laporan/eta", label: "Laporan ETA", icon: Ship, id: "report-eta" });
  if (role === "keuangan" || role === "purchasing")
    items.push({ to: "/laporan/hpp", label: "Laporan HPP & Margin", icon: Calculator, id: "report-hpp" });
  items.push({ to: "/laporan/proyeksi", label: "Laporan Proyeksi", icon: ClipboardList, id: "report-projection" });
  items.push({ to: "/laporan/akurasi", label: "Proyeksi vs Realisasi", icon: Target, id: "report-accuracy" });
  items.push({ to: "/peringatan-stock", label: "Peringatan Stock Minim", icon: AlertTriangle, id: "stock-alerts" });
  return items;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = navFor(user.role);

  const link = ({ isActive }) =>
    `flex items-center gap-2.5 px-4 py-2.5 text-sm border-l-2 transition-colors ${
      isActive
        ? "border-[#4338CA] bg-white text-[#4338CA] font-semibold"
        : "border-transparent text-neutral-300 hover:text-white hover:bg-white/5"
    }`;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <aside className="lg:w-64 bg-[#0a0a0a] lg:min-h-screen flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
          <div>
            <div className="font-display text-white font-extrabold text-base leading-tight">
              ERP STOCK
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-[#7c74ff]">
              {ROLE_LABEL[user.role]}
              {user.branch ? ` · ${user.branch}` : " · Pusat"}
            </div>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-1"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        <nav className={`${open ? "block" : "hidden"} lg:block py-3 flex-1`}>
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.id}`}
              className={link}
              onClick={() => setOpen(false)}
            >
              <it.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{it.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className={`${open ? "block" : "hidden"} lg:block border-t border-white/10 p-4`}>
          <div className="text-xs text-neutral-400 truncate">{user.email}</div>
          <button
            data-testid="logout-button"
            onClick={async () => {
              await logout();
              nav("/");
            }}
            className="mt-2 w-full flex items-center justify-center gap-2 h-9 border border-white/30 text-white text-xs uppercase tracking-wide hover:bg-white hover:text-black transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Keluar
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 bg-[#fafafa]">
        <Outlet />
      </main>
    </div>
  );
}
